import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  /**
   * Runs instead of the plain route change when this crumb is clicked.
   * A detail page that guards unsaved edits has to intercept the parent
   * crumb the same way it intercepts its own Back button — a bare `path`
   * is a react-router <Link> and walks straight past the dirty check.
   * Pass a *stable* callback (ref-backed); the crumb array is stored in
   * context, so a fresh closure each render would go stale.
   */
  onClick?: () => void;
}

interface TopToolbarContextType {
  leftContent: ReactNode | null;
  setLeftContent: (content: ReactNode | null) => void;
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (crumbs: BreadcrumbItem[]) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchPlaceholder: string;
  setSearchPlaceholder: (placeholder: string) => void;
  /** The slot at the right of the breadcrumb row that `ToolbarPageActions` renders into. */
  pageActionsEl: HTMLElement | null;
  setPageActionsEl: (el: HTMLElement | null) => void;
}

const TopToolbarContext = createContext<TopToolbarContextType>({
  leftContent: null,
  setLeftContent: () => {},
  breadcrumbs: [],
  setBreadcrumbs: () => {},
  searchQuery: '',
  setSearchQuery: () => {},
  searchPlaceholder: '',
  setSearchPlaceholder: () => {},
  pageActionsEl: null,
  setPageActionsEl: () => {},
});

export function TopToolbarProvider({ children }: { children: ReactNode }) {
  const [leftContent, setLeftContentState] = useState<ReactNode | null>(null);
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>([]);
  const [searchQuery, setSearchQueryState] = useState('');
  const [searchPlaceholder, setSearchPlaceholderState] = useState('');
  const [pageActionsEl, setPageActionsEl] = useState<HTMLElement | null>(null);

  const setLeftContent = useCallback((content: ReactNode | null) => {
    setLeftContentState(content);
  }, []);

  const setBreadcrumbs = useCallback((crumbs: BreadcrumbItem[]) => {
    setBreadcrumbsState(crumbs);
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
  }, []);

  const setSearchPlaceholder = useCallback((placeholder: string) => {
    setSearchPlaceholderState(placeholder);
  }, []);

  return (
    <TopToolbarContext.Provider value={{
      leftContent, setLeftContent, breadcrumbs, setBreadcrumbs,
      searchQuery, setSearchQuery, searchPlaceholder, setSearchPlaceholder,
      pageActionsEl, setPageActionsEl,
    }}>
      {children}
    </TopToolbarContext.Provider>
  );
}

/**
 * Hook for pages to opt into TopToolbar search.
 * Sets the placeholder on mount, clears query + placeholder on unmount.
 */
export function useToolbarSearch(placeholder: string) {
  const { searchQuery, setSearchQuery, setSearchPlaceholder } = useContext(TopToolbarContext);

  useEffect(() => {
    setSearchPlaceholder(placeholder);
  }, [placeholder, setSearchPlaceholder]);

  useEffect(() => {
    return () => {
      setSearchPlaceholder('');
      setSearchQuery('');
    };
  }, [setSearchPlaceholder, setSearchQuery]);

  return { searchQuery, setSearchQuery };
}

export function useTopToolbar() {
  return useContext(TopToolbarContext);
}

/**
 * Puts a page's compact controls (a time span, Refresh, Customize) at the right
 * of the top breadcrumb row, so the page does not spend a row of its own on
 * them. Renders nothing until the toolbar's slot exists (and outside the app
 * layout, e.g. in a unit test without the toolbar), so a page can mount it
 * unconditionally.
 *
 * Keep it to a few small controls: icon buttons with tooltips, a small select,
 * a short status. Anything wider belongs in the page's own PageToolbar.
 */
export function ToolbarPageActions({ children }: { children: ReactNode }) {
  const { pageActionsEl } = useContext(TopToolbarContext);
  if (!pageActionsEl) return null;
  return createPortal(children, pageActionsEl);
}

/**
 * The element `ToolbarPageActions` renders into. TopToolbar mounts one at the
 * right of the breadcrumb; a test can mount one to see a page's controls.
 */
export function ToolbarPageActionsSlot({ className }: { className?: string }) {
  const { setPageActionsEl } = useContext(TopToolbarContext);
  return <div className={className} ref={setPageActionsEl} />;
}
